import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles } from 'lucide-react';
import { generateMonthlyMoonData } from '@/utils/moonCalculations';

interface MoonYearlyCalendarViewProps {
  latitude: number;
  longitude: number;
  year: number;
  onMonthClick: (month: number) => void;
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const getIlluminationColorClass = (illumination: number, isSupermoon: boolean): string => {
  if (isSupermoon) return 'bg-amber-500 text-white ring-1 ring-yellow-400';
  if (illumination >= 90) return 'bg-amber-400 text-foreground font-medium';
  if (illumination >= 60) return 'bg-yellow-300 text-foreground font-medium';
  if (illumination >= 30) return 'bg-muted/40 text-foreground';
  return 'text-muted-foreground/50';
};

export const MoonYearlyCalendarView: React.FC<MoonYearlyCalendarViewProps> = ({
  latitude,
  longitude,
  year,
  onMonthClick,
}) => {
  const yearlyData = useMemo(() => {
    return Array.from({ length: 12 }, (_, monthIndex) => {
      const days = generateMonthlyMoonData(year, monthIndex, latitude, longitude);
      const firstDayOfWeek = new Date(year, monthIndex, 1).getDay();
      const hasSupermoon = days.some(d => d.isSupermoon);
      return { monthIndex, days, firstDayOfWeek, hasSupermoon };
    });
  }, [year, latitude, longitude]);

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 p-4 bg-card rounded-lg border">
        <span className="text-sm font-medium text-muted-foreground">Illumination:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-500 ring-1 ring-yellow-400"></div>
          <span className="text-xs">Supermoon</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-400"></div>
          <span className="text-xs">Full (90%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-300"></div>
          <span className="text-xs">Gibbous (60-89%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-muted/40"></div>
          <span className="text-xs">Quarter (30-59%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-muted/10 border border-border/50"></div>
          <span className="text-xs">Crescent (0-29%)</span>
        </div>
      </div>

      {/* 12-Month Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {yearlyData.map((month) => (
          <Card
            key={month.monthIndex}
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => onMonthClick(month.monthIndex)}
          >
            <CardHeader className="py-3 px-4 bg-primary/5">
              <CardTitle className="text-sm font-semibold text-center flex items-center justify-center gap-1.5">
                {monthNames[month.monthIndex]}
                {month.hasSupermoon && <Sparkles className="w-3.5 h-3.5 text-yellow-500" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayHeaders.map((day, i) => (
                  <div key={i} className="text-center text-xs font-medium text-muted-foreground">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: month.firstDayOfWeek }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square"></div>
                ))}
                {month.days.map((day) => (
                  <div
                    key={day.date}
                    className={`aspect-square flex items-center justify-center text-xs font-medium rounded ${getIlluminationColorClass(day.illumination, day.isSupermoon)}`}
                    title={`${monthNames[month.monthIndex]} ${day.date}: ${day.illumination}% ${day.moonPhaseName}${day.isSupermoon ? ' ⭐ Supermoon' : ''}`}
                  >
                    {day.date}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
